import {AccountsAPI, InitAccountResponse, OAuthAccessToken} from "./accounts-api";
import {OAuthCredential, UserCredential} from "@firebase/auth";
import {FacebookAuthProvider, GoogleAuthProvider, OAuthProvider} from "firebase/auth";
import {InitAccountFunc} from "../components/auth/AuthContext";
import {hashAccount} from "../components/auth/user";
import {CaptchaStore} from "../components/captcha/store";
import {AuthProviderType, isSsoProvider} from "../domain/providers";
import {getRedirectResult} from "../firebase";
import {gaCreatedAccount} from "../analytics/ga";
import {User} from "../components/auth/types";
import _debounce from "lodash/debounce";

export const doInitAccount = async (user: User) => {
  const currentHash = AccountHashStore.get();
  if (currentHash === hashAccount(user)) {
    return
  }

  console.info("running init account", user.uid, user.displayName)
  const idToken = await user.getIdToken();

  let accessToken: OAuthAccessToken | undefined = undefined
  if (isSsoProvider(user.provider)) {
    const userCredential = await getRedirectResult()
    accessToken = getAccessToken(userCredential)
  }
  const captchaToken = CaptchaStore.get() || undefined;
  const initAccountResponse = await AccountsAPI.initAccount({uid: user.uid, idToken, accessToken, captchaToken});
  AccountHashStore.set(initAccountResponse.accountHash);
  CaptchaStore.clear();
  emitAccountCreatedIfRequired(initAccountResponse, currentHash);
};

export const initAccount: InitAccountFunc = _debounce(
    async (user: User) => doInitAccount(user),
    2000,
    {leading: true},
);

const twoMinutes = 2 * 60 * 1000;

const emitAccountCreatedIfRequired = (initAccountResp: InitAccountResponse, currentHash: string | null) => {
  if (currentHash) {
    return
  }
  if (!initAccountResp.authProvider || initAccountResp.authProvider === "anonymous") {
    return
  }
  const accountCreated = Date.parse(initAccountResp.creationTime);
  const accountAge = Date.now() - accountCreated;
  if (accountAge < twoMinutes) {
    gaCreatedAccount(initAccountResp.authProvider as AuthProviderType);
  }
}

export const getAccessToken = (userCredentials: UserCredential | null): OAuthAccessToken | undefined => {
  if (!userCredentials) {
    return
  }
  const credentialFromResult = getAuthProvider(userCredentials.providerId)
  const credential = credentialFromResult(userCredentials)
  return toOAuthToken(credential);
}

const getAuthProvider = (providerId: string | null): ((userCredentials: UserCredential) => OAuthCredential | null) => {
  if (providerId === "google.com") {
    return GoogleAuthProvider.credentialFromResult;
  }
  if (providerId === "apple.com" || providerId === "microsoft.com") {
    return OAuthProvider.credentialFromResult
  }
  if (providerId === "facebook.com") {
    return FacebookAuthProvider.credentialFromResult
  }
  return () => null;
}

function toOAuthToken(credential?: OAuthCredential | null) {
  if (!credential?.accessToken) {
    return
  }
  return {
    token: credential.accessToken,
    provider: credential.providerId
  };
}

const accountHashKey = "wakelet-account-hash";
export const AccountHashStore = {
  set: (hash: string) => {
    localStorage.setItem(accountHashKey, hash);
  },
  get: (): string | null => {
    return localStorage.getItem(accountHashKey)
  },
  clear: () => {
    localStorage.removeItem(accountHashKey);
  }
}

